import { envWithDefault, envWithThrowIfMissing, logDev } from "utils/env";
import { SplitFactory } from "@splitsoftware/splitio";
import SplitIO from "@splitsoftware/splitio/types/splitio";
import { TrafficType } from "./types";
import * as Sentry from "@sentry/browser";

enum LoadingState {
  Loading,
  CacheHit,
  Timeout,
  Loaded,
}

export async function loadSplitClient(
  key: string,
  trafficType: TrafficType,
): Promise<{ client: SplitIO.IClient; manager: SplitIO.IManager }> {
  return new Promise((resolve, reject) => {
    let currentState = LoadingState.Loading;
    const loadStartTime = performance.now();

    logDev("SPLIT INITIALIZING", key);

    const splitFactory = SplitFactory({
      core: {
        authorizationKey: envWithThrowIfMissing("SPLIT_IO_CLIENT_API_KEY"),
        key,
        trafficType,
      },
      // Proxy configuration. Note that these URLs must be kept in sync with the proxy
      // configuration in https://github.com/chanzuckerberg/along-infra!
      urls: {
        sdk: "https://split.along.org/splitsdk",
        events: "https://split.along.org/splitevent",
        auth: "https://split.along.org/splitauth",
        streaming: "https://split.along.org/splitstream",
      },
      // Proxy configuration. The replacement value used here needs to be kept in sync with
      // the value in along-infra.
      url(target: unknown) {
        const url = Object.getPrototypeOf(this).url.call(this, target);
        return url.replace("testImpressions", "alongEventLog");
      },
      startup: {
        // We're intentionally blocking Along loading on the split SDK with the large timeout.
        // Experiment definitions get cached in localstorage so this timeout is really just for first
        // load.
        readyTimeout: parseFloat(envWithDefault("SPLIT_WAIT_TIME_S", "5")),
      },
      scheduler: {
        // Split default behaviour is to send impressions every 60 seconds (when available).
        // Shortening this interval is recommended to prevent data loss on navigate (i.e. when
        // there are impressions that haven't been sent yet).
        impressionsRefreshRate: 1,
        // TODO: We probably want to override eventsPushRate as well if we ever start using
        // split events.
        // eventsPushRate: 1,
      },
      storage: {
        // Allow split to store experiment metadata in localstorage. This speeds up subsequent page
        // loads, at the (relatively small) cost of not being able to guarantee people will receive
        // the correct treatment (in the case where new metadata hasn't loaded yet).
        type: "LOCALSTORAGE",
      },
      // Cast needed because 'url' key isn't documented in the IBrowserSettings interface :-/
    } as SplitIO.IBrowserSettings);

    const client = splitFactory.client();
    const manager = splitFactory.manager();

    client.once(client.Event.SDK_READY, () => {
      const loadTimeInS = (performance.now() - loadStartTime) / 1000;

      switch (currentState) {
        case LoadingState.Timeout:
          logDev("SPLIT CLIENT LOADED AFTER TIMEOUT", key, loadTimeInS);
          break;
        case LoadingState.CacheHit:
          logDev("SPLIT CLIENT LOADED AFTER CACHE", key, loadTimeInS);
          break;
        case LoadingState.Loading:
          logDev("SPLIT CLIENT READY", key, loadTimeInS);
          currentState = LoadingState.Loaded;
          resolve({ client, manager });
          break;
        default:
          reject(new Error(`unexpected state ${currentState}`));
      }
    });

    client.once(client.Event.SDK_READY_TIMED_OUT, () => {
      logDev("SPLIT CLIENT TIMED OUT", key);

      // Early warning in case Split ever goes down. If this happens consider flipping
      // KILLSWITCH_SPLIT. NOTE: if you're seeing this intermittently in Sentry that
      // doesn't necessarily mean there's a problem.
      // Split status can be found here: https://status.split.io/
      Sentry.captureMessage(`Split SDK timed out`);

      currentState = LoadingState.Timeout;
      resolve({ client, manager });
    });

    client.once(client.Event.SDK_READY_FROM_CACHE, () => {
      logDev("SPLIT CLIENT READY FROM CACHE", key);

      currentState = LoadingState.CacheHit;
      resolve({ client, manager });
    });
  });
}
